/**
 * DPL DEW - Pricing Engine
 * Framework-independent, deterministic financial computation engine.
 */

export const PRICING_ENGINE_VERSION = 'v2.1';

export type UomCategory = 'COUNT' | 'AREA' | 'LINEAR' | 'VOLUME' | 'WEIGHT' | 'UNKNOWN';

export const UOM_MAP: Record<string, UomCategory> = {
  sqft: 'AREA',
  sqm: 'AREA',
  sqyd: 'AREA',
  rft: 'LINEAR',
  rm: 'LINEAR',
  rmt: 'LINEAR',
  mtr: 'LINEAR',
  nos: 'COUNT',
  point: 'COUNT',
  pcs: 'COUNT',
  set: 'COUNT',
  lot: 'COUNT',
  unit: 'COUNT',
  cft: 'VOLUME',
  cbm: 'VOLUME',
  kg: 'WEIGHT',
  ltr: 'VOLUME',
  hr: 'COUNT',
  day: 'COUNT'
};

export function getUomCategory(uom?: string): UomCategory {
  if (!uom) return 'UNKNOWN';
  const normalized = uom.trim().toLowerCase();
  return UOM_MAP[normalized] || 'UNKNOWN';
}

export interface Dimensions {
  width?: number;
  height?: number;
  length?: number;
  unit?: 'mm' | 'cm' | 'm' | 'in' | 'ft';
  // Keep sqft here just for backwards compatibility typing
  sqft?: number; 
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateDimensions(dimensions: Dimensions | undefined | null, uomCategory: UomCategory): ValidationResult {
  const errors: string[] = [];
  if (uomCategory === 'COUNT' || uomCategory === 'UNKNOWN' || uomCategory === 'WEIGHT') {
    return { valid: true, errors: [] }; // Dimensions not strictly required
  }

  if (!dimensions) {
    return { valid: false, errors: ['Dimensions are required for this unit.'] };
  }

  const checkVal = (val: number | undefined, name: string) => {
    if (val === undefined || val === null) {
      errors.push(`Missing ${name}.`);
    } else if (isNaN(val) || !isFinite(val)) {
      errors.push(`Invalid ${name}.`);
    } else if (val < 0) {
      errors.push(`Negative ${name} is not allowed.`);
    } else if (val > 100000) {
      errors.push(`Absurd ${name} value.`);
    }
  };

  if (uomCategory === 'AREA') {
    checkVal(dimensions.width, 'width');
    checkVal(dimensions.height, 'height');
  } else if (uomCategory === 'LINEAR') {
    checkVal(dimensions.width, 'width (length)'); // we use width as primary linear dimension
  } else if (uomCategory === 'VOLUME') {
    checkVal(dimensions.width, 'width');
    checkVal(dimensions.height, 'height');
    checkVal(dimensions.length, 'length');
  }

  return { valid: errors.length === 0, errors };
}

// Convert any dimension to feet
export function convertToFeet(value: number, unit: Dimensions['unit']): number {
  if (!value || isNaN(value)) return 0;
  switch (unit) {
    case 'mm': return value / 304.8;
    case 'cm': return value / 30.48;
    case 'm': return value * 3.28084;
    case 'in': return value / 12;
    case 'ft': return value;
    default: return value / 30.48; // fallback to cm
  }
}

export function calculateLinear(width: number, unit: Dimensions['unit']): number {
  return convertToFeet(width, unit);
}

export function calculateArea(width: number, height: number, unit: Dimensions['unit']): number {
  const widthFt = convertToFeet(width, unit);
  const heightFt = convertToFeet(height, unit);
  return widthFt * heightFt;
}

export function calculateVolume(width: number, height: number, length: number, unit: Dimensions['unit']): number {
  return convertToFeet(width, unit) * convertToFeet(height, unit) * convertToFeet(length, unit);
}

export interface PricingModifier {
  id: string;
  type: 'wastage' | 'labor' | 'markup' | 'discount' | 'tax' | 'custom';
  mode: 'percentage' | 'multiplier' | 'fixed';
  value: number;
  appliesTo: 'material' | 'labor' | 'subtotal' | 'final';
  priority?: number;
}

export interface PricingRules {
  pricingModel: 'fixed' | 'area' | 'linear' | 'volume';
  modifiers?: PricingModifier[];
}

export interface CalculationTraceStep {
  label: string;
  formula: string;
}

export interface CalculationTrace {
  formula: string;
  inputs: Record<string, number | string>;
  steps: CalculationTraceStep[];
  output: number;
}

export interface NetTotalResult {
  total: number;
  multiplier: number;
  trace: CalculationTrace;
}

export function calculateNetTotal(
  quantity: number,
  rate: number,
  uom: string | undefined,
  dimensions: Dimensions | undefined | null,
  rules?: PricingRules
): NetTotalResult {
  const category = getUomCategory(uom);
  let multiplier = 1;
  let formula = '';
  const steps: CalculationTraceStep[] = [];
  
  const safeQty = quantity || 0;
  const safeRate = rate || 0;

  if (category === 'AREA' && dimensions?.width && dimensions?.height) {
    const areaSqft = calculateArea(dimensions.width, dimensions.height, dimensions.unit);
    multiplier = areaSqft;
    formula = 'qty × area × rate';
    steps.push({ label: 'Area', formula: `${dimensions.width} × ${dimensions.height} ${dimensions.unit} → ${areaSqft.toFixed(2)} sqft` });
  } else if (category === 'LINEAR' && dimensions?.width) {
    const linearFt = calculateLinear(dimensions.width, dimensions.unit);
    multiplier = linearFt;
    formula = 'qty × length × rate';
    steps.push({ label: 'Linear', formula: `${dimensions.width} ${dimensions.unit} → ${linearFt.toFixed(2)} ft` });
  } else if (category === 'VOLUME' && dimensions?.width && dimensions?.height && dimensions?.length) {
    const volCft = calculateVolume(dimensions.width, dimensions.height, dimensions.length, dimensions.unit);
    multiplier = volCft;
    formula = 'qty × width × height × length × rate';
    steps.push({ label: 'Volume', formula: `${dimensions.width} × ${dimensions.height} × ${dimensions.length} ${dimensions.unit} → ${volCft.toFixed(2)} cft` });
  } else {
    // COUNT, WEIGHT, UNKNOWN or missing dimensions
    multiplier = 1;
    formula = 'qty × rate';
  }

  // Raw math logic, don't round the intermediate total yet!
  let runningTotal = safeQty * multiplier * safeRate;
  steps.push({ label: 'Base Cost', formula: `${safeQty} × ${multiplier.toFixed(4)} × ${safeRate}` });

  if (rules?.modifiers?.length) {
    const sortedModifiers = [...rules.modifiers].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    for (const mod of sortedModifiers) {
      if (mod.mode === 'percentage') {
        runningTotal += runningTotal * (mod.value / 100);
        steps.push({ label: mod.type, formula: `${mod.value > 0 ? '+' : ''}${mod.value}%` });
        formula += ` × (1 + ${mod.value}%)`;
      } else if (mod.mode === 'multiplier') {
        runningTotal *= mod.value;
        steps.push({ label: mod.type, formula: `×${mod.value}` });
        formula += ` × ${mod.value}`;
      } else if (mod.mode === 'fixed') {
        runningTotal += mod.value;
        steps.push({ label: mod.type, formula: `${mod.value > 0 ? '+' : ''}${mod.value} fixed` });
        formula += ` + ${mod.value}`;
      }
    }
  }

  return {
    total: runningTotal,
    multiplier,
    trace: {
      formula,
      inputs: {
        qty: safeQty,
        multiplier,
        rate: safeRate,
        uom: uom || 'unknown'
      },
      steps,
      output: runningTotal
    }
  };
}

export function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface QuotationFinancialResult {
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  taxableAmount: number;
  grandTotal: number;
}

export function calculateQuotationTotals(
  sectionSubtotals: number[],
  taxRate: number,
  taxEnabled: boolean,
  discountValue: number,
  discountType: 'percentage' | 'fixed'
): QuotationFinancialResult {
  // Raw precision sum
  const rawSubtotal = sectionSubtotals.reduce((sum, val) => sum + val, 0);

  let rawDiscountAmount = 0;
  if (discountValue) {
    rawDiscountAmount = discountType === 'percentage'
      ? (rawSubtotal * discountValue) / 100
      : discountValue;
  }

  const rawTaxableAmount = rawSubtotal - rawDiscountAmount;
  const rawTaxAmount = taxEnabled ? (rawTaxableAmount * taxRate) / 100 : 0;
  const rawGrandTotal = rawTaxableAmount + rawTaxAmount;

  return {
    subtotal: roundCurrency(rawSubtotal),
    taxAmount: roundCurrency(rawTaxAmount),
    discountAmount: roundCurrency(rawDiscountAmount),
    taxableAmount: roundCurrency(rawTaxableAmount),
    grandTotal: roundCurrency(rawGrandTotal)
  };
}
